import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Dialog, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';

// "How it works" modal — opened from every create entry point ("Create your own" rail tiles, the
// header Create action, and the My-creations "Create another" tile). Shows the explainer illustration
// (its title + 3 steps are baked into the image) and a single "Get started" button into the wizard.
// Matches the approved wireframe (docs/mockups/app-wireframe.html .howmodal). Close via X / backdrop / Esc.
const HOW_IMAGE = `${process.env.PUBLIC_URL || ''}/images/create-how-it-works.webp`;

// Fallback steps shown only if the baked-in illustration is missing (the real .webp drops in later).
const STEPS = [
  ['1', 'Tell us what you’d like to see', 'A feeling, a style, an optional photo — your choices.'],
  ['2', 'We design a few options', 'Unique AI cover options to choose from.'],
  ['3', 'We print and mail it', 'Real card stock, sent with care.'],
];

const HowItWorksModal = ({ open, onClose, onGetStarted }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [imgError, setImgError] = useState(false);

  const handleGetStarted = () => {
    if (onClose) onClose();
    if (onGetStarted) {
      onGetStarted();
    } else {
      navigate('/create');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      data-testid="how-it-works-modal"
      aria-label="How creating a card works"
      BackdropProps={{ sx: { backgroundColor: 'rgba(26,34,44,0.55)' } }}
      PaperProps={{
        sx: {
          backgroundColor: '#FEFDF6',
          borderRadius: fullScreen ? 0 : '14px',
          border: fullScreen ? 'none' : '0.5px solid #e7e3d6',
          boxShadow: '0 18px 54px rgba(20,28,36,0.34)',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 2,
            width: 32,
            height: 32,
            color: '#5b6b72',
            backgroundColor: 'rgba(255,255,255,0.85)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
          }}
        >
          <CloseIcon />
        </IconButton>

        {imgError ? (
          <Box sx={{ p: '44px 22px 8px' }}>
            <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, mb: 0.5 }}>
              Design a one-of-a-kind cover
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2, fontSize: '0.95rem' }}>
              Here’s how it works.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {STEPS.map(([n, title, desc]) => (
                <Box key={n} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      flex: '0 0 auto',
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      backgroundColor: '#e1f0f3',
                      color: '#206884',
                      fontWeight: 700,
                      fontSize: 13,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    {n}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{title}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>{desc}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: '18px 18px 0' }}>
            <Box
              component="img"
              src={HOW_IMAGE}
              onError={() => setImgError(true)}
              alt="How it works: tell us what you'd like to see, we design a few options, then print and mail it."
              sx={{ width: '100%', display: 'block', borderRadius: '10px' }}
            />
          </Box>
        )}

        <Box sx={{ p: '18px 22px 22px' }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleGetStarted}
            sx={{
              backgroundColor: '#206884',
              color: '#fff',
              boxShadow: 'none',
              fontWeight: 600,
              py: 1.5,
              '&:hover': { backgroundColor: '#185166', boxShadow: 'none' },
            }}
          >
            Get started
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default HowItWorksModal;
